import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useAuth, authHeaders } from '../contexts/AuthContext';
import {
  Shield, TrendingUp, Target, Brain, Zap, CheckCircle2,
  AlertCircle, Flame, Star, MessageSquare, Loader2, ArrowUpRight
} from 'lucide-react';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fadeUp = { hidden: { opacity: 0, y: 12 }, visible: { opacity: 1, y: 0 } };

function DimensionBar({ label, value, color, icon: Icon }) {
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-muted-foreground flex-shrink-0" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium">{label}</span>
          <span className="text-xs font-semibold" style={{ color }}>{value}</span>
        </div>
        <div className="w-full h-1.5 rounded-full bg-secondary">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${value}%` }}
            transition={{ duration: 0.8, ease: 'easeOut' }}
            className="h-full rounded-full"
            style={{ backgroundColor: color }}
          />
        </div>
      </div>
    </div>
  );
}

function InsightCard({ icon: Icon, title, content, variant = 'default' }) {
  const colors = {
    good: 'border-emerald-500/20 bg-emerald-500/5',
    warning: 'border-amber-500/20 bg-amber-500/5',
    info: 'border-blue-500/20 bg-blue-500/5',
    default: 'border-border/50 bg-card',
  };
  return (
    <div className={`p-4 rounded-xl border ${colors[variant]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
        <span className="text-xs font-semibold">{title}</span>
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{content}</p>
    </div>
  );
}

export default function AinaaInsightsPage() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [aiInsight, setAiInsight] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [chatMsg, setChatMsg] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [sessionId] = useState(`intro_${Date.now()}`);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, insightRes] = await Promise.all([
        fetch(`${API}/ainaa/trust-profile`, { headers: authHeaders() }),
        fetch(`${API}/spectre/insights/${user?.ainaa_id}`, { headers: authHeaders() }),
      ]);
      if (profileRes.ok) setProfile(await profileRes.json());
      if (insightRes.ok) setAiInsight(await insightRes.json());
    } catch {}
    setLoading(false);
  }, [user?.ainaa_id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRecompute = async () => {
    setRecomputing(true);
    try {
      const res = await fetch(`${API}/ainaa/trust-profile/recompute`, {
        method: 'POST', headers: authHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setProfile(prev => ({ ...prev, dimensions: data.dimensions, trust_score: data.overall_score }));
      }
    } catch {}
    setRecomputing(false);
  };

  const handleChat = async (e) => {
    e.preventDefault();
    if (!chatMsg.trim() || chatLoading) return;
    const msg = chatMsg.trim();
    setChatMsg('');
    setChatHistory(prev => [...prev, { role: 'user', text: msg }]);
    setChatLoading(true);
    try {
      const res = await fetch(`${API}/ainaa/introwspect/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ message: msg, session_id: sessionId }),
      });
      const data = await res.json();
      setChatHistory(prev => [...prev, { role: 'ai', text: data.response || 'I couldn\'t process that.' }]);
    } catch {
      setChatHistory(prev => [...prev, { role: 'ai', text: 'Connection error. Try again.' }]);
    }
    setChatLoading(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center py-20">
      <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
    </div>
  );

  const dims = profile?.dimensions || {};
  const trustScore = profile?.trust_score || user?.trust_score || 70;

  const getDimColor = (v) => v >= 85 ? '#10B981' : v >= 70 ? '#F59E0B' : '#EF4444';
  const getTier = (s) => s >= 90 ? 'Exceptional' : s >= 80 ? 'Strong' : s >= 70 ? 'Good' : s >= 60 ? 'Building' : 'New';

  return (
    <motion.div initial="hidden" animate="visible" variants={{ visible: { transition: { staggerChildren: 0.06 } } }} data-testid="ainaa-insights-page">
      {/* Header */}
      <motion.div variants={fadeUp} className="mb-8">
        <div className="flex items-center gap-3 mb-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" strokeWidth={2} />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-medium tracking-tight">AINAA Insights</h1>
            <p className="text-xs text-muted-foreground font-mono">{user?.ainaa_id}</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground mt-2">
          Your private trust profile. Powered by Introwspect.
        </p>
      </motion.div>

      {/* Trust Score Hero */}
      <motion.div variants={fadeUp} className="p-6 rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-500/5 to-transparent mb-6">
        <div className="flex items-center gap-6">
          <div className="relative">
            <svg width="100" height="100" viewBox="0 0 100 100">
              <circle cx="50" cy="50" r="42" fill="none" stroke="currentColor" strokeWidth="3" className="text-border" />
              <motion.circle
                cx="50" cy="50" r="42" fill="none"
                stroke={getDimColor(trustScore)}
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${2 * Math.PI * 42}`}
                initial={{ strokeDashoffset: 2 * Math.PI * 42 }}
                animate={{ strokeDashoffset: 2 * Math.PI * 42 * (1 - trustScore / 100) }}
                transition={{ duration: 1, ease: 'easeOut' }}
                transform="rotate(-90 50 50)"
              />
              <text x="50" y="46" textAnchor="middle" className="fill-foreground text-2xl font-bold" style={{ fontSize: '24px', fontWeight: 700 }}>{trustScore}</text>
              <text x="50" y="62" textAnchor="middle" className="fill-muted-foreground" style={{ fontSize: '9px' }}>TRUST SCORE</text>
            </svg>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-lg font-semibold">{getTier(trustScore)}</span>
              <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${trustScore >= 80 ? 'bg-emerald-500/10 text-emerald-600' : trustScore >= 70 ? 'bg-amber-500/10 text-amber-600' : 'bg-red-500/10 text-red-600'}`}>
                Top {Math.max(5, 100 - trustScore)}%
              </span>
            </div>
            {aiInsight?.insight && (
              <p className="text-sm text-muted-foreground leading-relaxed">{aiInsight.insight}</p>
            )}
            <Button variant="outline" size="sm" className="mt-3 h-7 text-[11px] gap-1.5" onClick={handleRecompute} disabled={recomputing}>
              {recomputing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
              Recompute Trust Score
            </Button>
          </div>
        </div>
      </motion.div>

      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        {/* Trust Dimensions */}
        <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card">
          <h3 className="text-sm font-semibold mb-4">Trust Dimensions</h3>
          <div className="space-y-4">
            <DimensionBar label="Reliability" value={dims.reliability || 70} color={getDimColor(dims.reliability || 70)} icon={CheckCircle2} />
            <DimensionBar label="Skill Quality" value={dims.skill_quality || 70} color={getDimColor(dims.skill_quality || 70)} icon={Star} />
            <DimensionBar label="Professionalism" value={dims.professionalism || 70} color={getDimColor(dims.professionalism || 70)} icon={TrendingUp} />
            <DimensionBar label="Collaboration" value={dims.collaboration || 70} color={getDimColor(dims.collaboration || 70)} icon={Target} />
            <DimensionBar label="Discipline" value={dims.discipline || 70} color={getDimColor(dims.discipline || 70)} icon={Flame} />
          </div>
        </motion.div>

        {/* AI Insights */}
        <motion.div variants={fadeUp} className="space-y-3">
          {aiInsight?.strengths?.length > 0 && (
            <InsightCard
              icon={TrendingUp}
              title="What's going great"
              content={aiInsight.strengths.join(' · ')}
              variant="good"
            />
          )}
          {aiInsight?.growth_areas?.length > 0 && (
            <InsightCard
              icon={AlertCircle}
              title="Areas to grow"
              content={aiInsight.growth_areas.join(' · ')}
              variant="warning"
            />
          )}
          {aiInsight?.collaboration_style && (
            <InsightCard
              icon={Brain}
              title="Your work style"
              content={`${aiInsight.collaboration_style}${aiInsight.recommended_for?.length ? '. Best suited for: ' + aiInsight.recommended_for.join(', ') : ''}`}
              variant="info"
            />
          )}
          {/* Stats */}
          <div className="grid grid-cols-3 gap-3">
            {[
              { label: 'Reflections', value: profile?.total_reflections || 0, icon: Brain },
              { label: 'Goals', value: profile?.total_goals || 0, icon: Target },
              { label: 'Habits', value: profile?.total_habits || 0, icon: Flame },
            ].map(s => (
              <div key={s.label} className="p-3 rounded-xl border border-border/50 bg-card text-center">
                <s.icon className="w-4 h-4 mx-auto mb-1 text-muted-foreground" strokeWidth={1.5} />
                <p className="text-lg font-semibold">{s.value}</p>
                <p className="text-[10px] text-muted-foreground">{s.label}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>

      {/* Introwspect Chat */}
      <motion.div variants={fadeUp} className="p-5 rounded-xl border border-border/50 bg-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 rounded-md bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
            <Brain className="w-3 h-3 text-white" strokeWidth={2} />
          </div>
          <h3 className="text-sm font-semibold">Introwspect</h3>
          <span className="text-[10px] text-muted-foreground">Your private AI companion</span>
        </div>

        <div className="min-h-[120px] max-h-[300px] overflow-y-auto mb-3 space-y-2">
          {chatHistory.length === 0 && (
            <div className="text-center py-6">
              <Brain className="w-8 h-8 mx-auto mb-2 text-muted-foreground/30" strokeWidth={1.5} />
              <p className="text-xs text-muted-foreground">Ask Introwspect anything about your growth, goals, or trust score.</p>
              <div className="flex flex-wrap justify-center gap-1.5 mt-3">
                {['How can I improve my trust score?', 'What should I focus on this week?', 'Give me a growth plan'].map(q => (
                  <button
                    key={q}
                    onClick={() => { setChatMsg(q); }}
                    className="px-2.5 py-1 rounded-full border border-border/60 text-[10px] text-muted-foreground hover:text-foreground hover:border-border transition-all"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}
          {chatHistory.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[80%] px-3 py-2 rounded-xl text-sm ${
                msg.role === 'user'
                  ? 'bg-foreground text-background'
                  : 'bg-secondary text-foreground'
              }`}>
                {msg.text}
              </div>
            </div>
          ))}
          {chatLoading && (
            <div className="flex justify-start">
              <div className="px-3 py-2 rounded-xl bg-secondary">
                <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
              </div>
            </div>
          )}
        </div>

        <form onSubmit={handleChat} className="flex gap-2">
          <input
            value={chatMsg}
            onChange={e => setChatMsg(e.target.value)}
            placeholder="Ask Introwspect..."
            className="flex-1 h-9 px-3 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-1 focus:ring-amber-500/50"
            data-testid="introwspect-input"
          />
          <Button type="submit" size="sm" className="h-9 px-3" disabled={chatLoading || !chatMsg.trim()}>
            <ArrowUpRight className="w-4 h-4" />
          </Button>
        </form>
      </motion.div>
    </motion.div>
  );
}
